import { useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { connect, useDispatch } from 'react-redux';

import { ticketActivitiesSet, ticketParticipantsSet } from '~/actions/ticket';
import {
  addTicketParticipants,
  getActivities,
  removeTicketParticipant,
} from '~/api/tickets';
import UserAvatar from '~/components/Client/UserAvatar';
import UserName from '~/components/Client/UserName';
import { showToast } from '~/components/Shared/EcToast/showToast';
import { User } from '~/components/Shared/User/User';
import UserSelect from '~/components/Shared/UserSelect/UserSelect';
import { Box } from '~/eds/src/components/Box/Box';
import { Button } from '~/eds/src/components/Button/Button';
import { ContentContainer } from '~/eds/src/components/ContentContainer/ContentContainer';
import { FormField } from '~/eds/src/components/FormField/FormField';
import { useModal } from '~/eds/src/components/hooks/useModal';
import { Icon } from '~/eds/src/components/Icon/Icon';
import { IconButton } from '~/eds/src/components/IconButton/IconButton';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { SearchInput } from '~/eds/src/components/SearchInput/SearchInput';
import { StatusMessage } from '~/eds/src/components/StatusMessage/StatusMessage';
import { Text } from '~/eds/src/components/Text/Text';
import { PilotId } from '~/eds/src/types/PilotId';
import { User as EdsUser } from '~/eds/src/types/User';
import { Uuid } from '~/eds/src/types/Uuid';
import { testHasTextMatch } from '~/eds/src/utils/testHasTextMatch';
import { EntityType } from '~/enums/EntityType';
import { WorkflowTicketRoleType } from '~/enums/WorkflowTicketRoleType';
import { useAsync } from '~/hooks/useAsync';
import { useResolveUsers } from '~/hooks/useResolveUsers';
import { api } from '~/redux/api/createApi';
import * as toastTypes from '~/types/toast.types';
import { getUserName } from '~/utils/user';

interface TicketContributorListProps {
  label: string;
  contributors: EdsUser[];
  onRemoveContributor?: (id: PilotId) => void;
  isEditing?: boolean;
  roles?: any;
}

type UserRole = Record<PilotId, { role: string; id: PilotId }>;

interface TicketParticipantsProps {
  ticketId: Uuid;
  //TODO: We still have to figure the ticket type
  ticket: any;
  ticketActivitiesSet: (data: any) => void;
  ticketParticipantsSet: (data: any) => void;
}

const TicketContributorList = ({
  label,
  contributors,
  onRemoveContributor,
  isEditing,
  roles,
}: TicketContributorListProps) => {
  const intl = useIntl();
  if (contributors.length === 0) {
    return null;
  }

  return (
    <Layout direction="column" spacing={4}>
      <Text variant="tiny" color="text.quiet">
        {label}
      </Text>
      {contributors.map((contributor) => {
        const enableRemove =
          roles[contributor.id]?.role === WorkflowTicketRoleType.Participant;
        return (
          <Layout
            align="center"
            key={contributor.id}
            justify="space-between"
            mb={4}
          >
            <Layout align="center" spacing={2}>
              <UserAvatar userId={contributor.id} />
              <Text variant="body">
                <UserName userId={contributor.id} />
              </Text>
            </Layout>
            {isEditing && (
              <IconButton
                disabled={!enableRemove}
                icon="trash"
                text={intl.formatMessage(
                  {
                    id: 'TicketParticipants.removeParticipant',
                    defaultMessage: 'remove participant {contributorId}',
                    description: 'Icon button text to remove a participant.',
                  },
                  { contributorId: contributor.id },
                )}
                tooltip={
                  enableRemove
                    ? intl.formatMessage({
                        id: 'TicketParticipants.removeParticipantTooltip',
                        defaultMessage: 'Remove participant',
                        description:
                          'Tooltip for the remove participant button.',
                      })
                    : intl.formatMessage({
                        id: 'TicketParticipants.cannotRemoveParticipantTooltip',
                        defaultMessage:
                          'This user has roles assigned to them in this ticket and cannot be removed.',
                        description:
                          'Tooltip explaining why a participant cannot be removed.',
                      })
                }
                onClick={() => onRemoveContributor?.(contributor.id as number)}
              />
            )}
          </Layout>
        );
      })}
    </Layout>
  );
};

const filterContributors = (
  contributors: EdsUser[],
  roles: UserRole,
  searchText: string,
) => {
  const filteredContributors = searchText.length
    ? contributors.filter(
        (contributor) =>
          contributor.firstName &&
          contributor.lastName &&
          (testHasTextMatch(contributor.firstName.toLowerCase(), searchText) ||
            testHasTextMatch(contributor.lastName.toLowerCase(), searchText)),
      )
    : contributors;
  const isAssignee = (user: EdsUser) =>
    roles[user.id as number]?.role === WorkflowTicketRoleType.Assignee;
  return {
    assignees: filteredContributors.filter(isAssignee),
    participants: filteredContributors.filter((user) => !isAssignee(user)),
  };
};

export const TicketParticipantsComponent = ({
  ticketId,
  // connected props
  ticket,
  ticketActivitiesSet,
  ticketParticipantsSet,
}: TicketParticipantsProps) => {
  const intl = useIntl();
  const [userIds, setNewUserIDs] = useState<PilotId[]>([]);
  const [searchText, setSearchText] = useState('');
  const [participantToRemove, setParticipantToRemove] = useState<PilotId>();
  const [isEditing, setEditMode] = useState(false);
  const [isRemovingParticipant, setIsRemovingParticipant] = useState(false);
  const dispatch = useDispatch();

  const { users: resolvedUsers = {}, isLoading: isLoadingUsers } =
    useResolveUsers({
      userIds: [...Object.keys(ticket.participants), ...userIds],
      skip: !ticket?.participants,
    });

  const contributors = useMemo(() => {
    return Object.values(resolvedUsers).filter(
      (user) => ticket.participants[user.id],
    );
  }, [resolvedUsers]);

  const roles = ticket.participants;
  const user = participantToRemove && resolvedUsers[participantToRemove];

  const {
    data: ticketParticipants,
    refetch: fetchParticipants,
    isError,
    isFetching,
  } = api.endpoints.getTicketParticipants.useQuery(
    {
      ticketId,
    },
    { skip: !ticketId },
  );

  useEffect(() => {
    if (ticketParticipants) {
      ticketParticipantsSet(ticketParticipants.data);
    }
  }, [ticketParticipants]);

  function handleParticipantRemove(id: PilotId) {
    setParticipantToRemove(id);
    showModal();
  }

  function getUserNameText(userIds: PilotId[]) {
    const userNames = userIds.map((id) =>
      getUserName(resolvedUsers[id] || {}, intl),
    );
    if (userNames.length === 1) return userNames[0];
    else if (userNames.length === 2)
      return intl.formatMessage(
        {
          id: 'TicketParticipants.userNamesTwo',
          defaultMessage: '{user1} and {user2}',
          description:
            'Formats two user names with and, user1 and user2 are placeholders.',
        },
        { user1: userNames[0], user2: userNames[1] },
      );
    else
      return intl.formatMessage(
        {
          id: 'TicketParticipants.userNamesMultiple',
          defaultMessage: '{user1} and {count} other people',
          description:
            'Formats multiple user names, user1 is the first user, count is the number of additional users.',
        },
        { user1: userNames[0], count: userNames.length - 1 },
      );
  }

  const { executor: getTicketActivities } = useAsync(
    getActivities,
    { entityId: ticketId, entityType: EntityType.Ticket, status: '' },
    {
      successHandler: ticketActivitiesSet,
    },
  );

  const { executor: addParticipants, isLoading: isAddingParticipants } =
    useAsync(
      addTicketParticipants,
      { ticketId, userIds },
      {
        successToastMessage: intl.formatMessage(
          {
            id: 'TicketParticipants.addSuccess',
            defaultMessage:
              '{userNames} {count, plural, one {has} other {have}} been added to the list of participants.',
            description:
              'Success message when participants are added to a ticket.',
          },
          {
            userNames: getUserNameText(userIds),
            count: userIds.length,
          },
        ),
        successHandler: async ({ data }) => {
          setNewUserIDs([]);
          ticketParticipantsSet(data);
          setEditMode(!isEditing);
          fetchParticipants();
          getTicketActivities();
          // @ts-ignore
          dispatch(api.util.invalidateTags([{ type: 'Acl', id: 'LIST' }]));
        },
        errorToastMessage: intl.formatMessage({
          id: 'TicketParticipants.addError',
          defaultMessage: 'Adding participants failed.',
          description: 'Error message when adding participants fails.',
        }),
      },
    );

  function handleFilterChange(searchText: string) {
    setSearchText(searchText);
  }

  const [modal, showModal, hideModal] = useModal({
    title: intl.formatMessage({
      id: 'TicketParticipants.removeTitle',
      defaultMessage: 'Remove Participant',
      description: 'Modal title for removing a participant from a ticket',
    }),
    primaryAction: {
      text: intl.formatMessage({
        id: 'TicketParticipants.remove',
        defaultMessage: 'Remove',
        description:
          'Button text to remove a participant from a ticket in the workflow',
      }),
      variant: 'danger',
      isLoading: isRemovingParticipant,
      onClick: async () => {
        try {
          setIsRemovingParticipant(true);
          await removeTicketParticipant(ticketId, participantToRemove);
          fetchParticipants();
          getTicketActivities();
          setEditMode(false);
          // @ts-ignore
          dispatch(api.util.invalidateTags([{ type: 'Acl', id: 'LIST' }]));
          showToast(
            toastTypes.SUCCESS,
            intl.formatMessage(
              {
                id: 'TicketParticipants.removeSuccess',
                defaultMessage: '{user} has been removed as a participant.',
                description:
                  'Success message when a participant is removed from a ticket. Example: user = "John Smith"',
              },
              {
                user: getUserName(user, intl),
              },
            ),
          );
        } catch {
          showToast(
            toastTypes.ERROR,
            intl.formatMessage({
              id: 'TicketParticipants.removeError',
              defaultMessage: 'Remove participant failed. Please try again.',
              description: 'Error message when removing a participant fails.',
            }),
          );
        } finally {
          setIsRemovingParticipant(false);
          hideModal();
        }
      },
    },
    children: (
      <div>
        {intl.formatMessage({
          id: 'TicketParticipants.removeMessage',
          defaultMessage: 'Are you sure you want to remove participant ',
          description:
            'Message displayed in the modal when removing a participant from a ticket',
        })}
        {user && <User mode="name" user={user} />}?
      </div>
    ),
  });

  const { assignees, participants } = useMemo(
    () => filterContributors(contributors, roles, searchText),
    [contributors, roles, searchText],
  );

  if (isError) {
    return (
      <Box mb={4}>
        <StatusMessage
          message={intl.formatMessage({
            id: 'TicketParticipants.loadError',
            defaultMessage: 'An error occurred while loading participants.',
            description: 'Error message when loading participants fails.',
          })}
          status="danger"
        />
      </Box>
    );
  }

  return (
    <ContentContainer
      loadingContent={{ isLoading: isLoadingUsers || isFetching }}
    >
      <Box>
        {modal}
        <Layout align="center" mb={2} justify="space-between">
          <Layout align="center">
            <Icon icon="department" />
            <Text variant="body-bold" ml={1}>
              <FormattedMessage
                id="TicketParticipants.contributors"
                defaultMessage="Contributors"
                description="Header for the contributors section."
              />
            </Text>
          </Layout>
          <Layout justify="flex-end">
            <Button
              variant="action"
              text={intl.formatMessage({
                id: 'TicketParticipants.edit',
                defaultMessage: 'Edit',
                description: 'Button text to edit participants.',
              })}
              icon="edit"
              iconPosition="left"
              onClick={() => setEditMode(!isEditing)}
            />
          </Layout>
        </Layout>
        <Box mt={4}>
          <SearchInput
            name="filter-contributors"
            value={searchText}
            onChange={(searchText) => {
              handleFilterChange(searchText || '');
            }}
            placeholder={intl.formatMessage({
              id: 'TicketParticipants.filterContributors',
              defaultMessage: 'Filter Contributors',
              description: 'Placeholder for the filter contributors input.',
            })}
          />
        </Box>
        <Box mb={4} mt={6}>
          {assignees.length + participants.length > 0 ? (
            <Layout direction="column" space={4}>
              <TicketContributorList
                roles={roles}
                label={intl.formatMessage({
                  id: 'TicketParticipants.assignees',
                  defaultMessage: 'Assignees',
                  description: 'Label for the assignees list.',
                })}
                contributors={assignees}
                isEditing={isEditing}
              />
              <TicketContributorList
                roles={roles}
                label={intl.formatMessage({
                  id: 'TicketParticipants.participants',
                  defaultMessage: 'Participants',
                  description: 'Label for the participants list.',
                })}
                contributors={participants}
                isEditing={isEditing}
                onRemoveContributor={handleParticipantRemove}
              />
            </Layout>
          ) : searchText ? (
            <Box p="20">
              <Text variant="tiny">
                <FormattedMessage
                  id="TicketParticipants.noUsersMatch"
                  defaultMessage="No users matched your search."
                  description="Message when no users match the search criteria."
                />
              </Text>
            </Box>
          ) : null}
        </Box>
        {isEditing ? (
          <Box mb={2}>
            <FormField
              input={UserSelect}
              label={intl.formatMessage({
                id: 'TicketParticipants.users',
                defaultMessage: 'Users',
                description: 'Label for the user select input.',
              })}
              name="select-role"
              disabled={isAddingParticipants}
              inputProps={{
                isMulti: true,
                notAvailableUsers: Object.keys(ticket.participants).map((key) =>
                  Number(key),
                ),
              }}
              value={userIds}
              onChange={(selectedUsers) =>
                setNewUserIDs((selectedUsers || []) as PilotId[])
              }
            />
            {userIds.length ? (
              <Layout my={2}>
                <Button
                  variant="action"
                  text={intl.formatMessage({
                    id: 'TicketParticipants.cancel',
                    defaultMessage: 'Cancel',
                    description: 'Button text to cancel an action.',
                  })}
                  onClick={() => setNewUserIDs([])}
                />
                <Button
                  variant="action"
                  text={intl.formatMessage({
                    id: 'TicketParticipants.save',
                    defaultMessage: 'Save',
                    description: 'Button text to save changes.',
                  })}
                  icon="check"
                  iconPosition="left"
                  onClick={addParticipants}
                  isLoading={isAddingParticipants}
                />
              </Layout>
            ) : null}
          </Box>
        ) : null}
      </Box>
    </ContentContainer>
  );
};

function mapStateToProps({ ticket }: any) {
  return {
    ticket,
  };
}

export const TicketParticipants = connect(mapStateToProps, {
  ticketActivitiesSet,
  ticketParticipantsSet,
})(TicketParticipantsComponent);
